import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { recordAuthEvent } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

/**
 * Catch-all rescue route for OAuth callback paths that don't have a dedicated
 * page — e.g. `/~oauth`, `/auth/callback`, or any redirect Supabase/Lovable
 * may emit after a Google sign-in. Instead of showing a 404, we:
 *
 *   1. Implicit flow: parse `access_token` / `refresh_token` from URL hash or
 *      query and call `supabase.auth.setSession(...)`.
 *   2. PKCE flow: parse `?code=...` and call
 *      `supabase.auth.exchangeCodeForSession(code)`.
 *   3. If neither is present after 5s, redirect to `/auth` with a toast.
 *
 * Used on web AND inside the Capacitor WebView as the landing pad for
 * `https://fitauraai.com/~oauth` after the Chrome Custom Tab returns.
 */
export const OAuthCatchAll = () => {
  const { session, loading } = useAuth();
  const location = useLocation();
  const { toast } = useToast();
  const [hydrating, setHydrating] = useState(true);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const hashRaw = window.location.hash.startsWith("#")
      ? window.location.hash.slice(1)
      : window.location.hash;
    const hashParams = new URLSearchParams(hashRaw);
    const queryParams = new URLSearchParams(location.search);

    const access_token =
      hashParams.get("access_token") ?? queryParams.get("access_token");
    const refresh_token =
      hashParams.get("refresh_token") ?? queryParams.get("refresh_token");
    const code = queryParams.get("code");

    // Persist the raw URL (sanitized) for diagnostics.
    try {
      const sanitized = `${window.location.pathname}${
        queryParams.toString() ? `?[${Array.from(queryParams.keys()).join(",")}]` : ""
      }${hashRaw ? `#[${Array.from(hashParams.keys()).join(",")}]` : ""}`;
      window.sessionStorage.setItem(
        "fitaura.auth.lastCallbackUrl",
        window.location.href,
      );
      window.sessionStorage.setItem("fitaura.auth.lastCallbackSanitized", sanitized);
    } catch {
      /* noop */
    }

    // 5-second safety timeout: if nothing resolves, bail out to /auth.
    const timeoutId = window.setTimeout(() => {
      if (cancelled) return;
      recordAuthEvent("callback-timeout", "no-tokens-after-5s");
      setTimedOut(true);
      toast({
        title: "Sign-in didn't complete",
        description: "Please try again.",
        variant: "destructive",
      });
    }, 5000);

    (async () => {
      if (access_token && refresh_token) {
        recordAuthEvent("callback-implicit");
        try {
          await supabase.auth.setSession({ access_token, refresh_token });
          recordAuthEvent("set-session-success", "implicit");
        } catch (e) {
          console.error("[oauth-catchall] setSession failed", e);
          recordAuthEvent(
            "set-session-failure",
            e instanceof Error ? e.message : "implicit",
          );
        }
      } else if (code) {
        recordAuthEvent("callback-pkce");
        try {
          await supabase.auth.exchangeCodeForSession(code);
          recordAuthEvent("set-session-success", "pkce");
        } catch (e) {
          console.error("[oauth-catchall] exchangeCodeForSession failed", e);
          recordAuthEvent(
            "set-session-failure",
            e instanceof Error ? e.message : "pkce",
          );
        }
      }
      if (!cancelled) {
        window.clearTimeout(timeoutId);
        setHydrating(false);
      }
    })();

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [location.search, toast]);

  if (timedOut && !session) {
    return <Navigate to="/auth" replace />;
  }

  if (hydrating || loading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-background text-foreground">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Finishing Google sign-in…</p>
      </div>
    );
  }

  return <Navigate to={session ? "/app" : "/auth"} replace />;
};

export default OAuthCatchAll;
